import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { UserType } from '../graphql/generated/graphql';
import clearIcon from '../icons/non-prefilled/16x16/clear.svg';
import InvitationModal from '../invitation/InvitationModal';
import { inputToOptions } from '../invitation/InvitationUserSelect';
import useInvitationEmailOption from '../invitation/useInvitationEmailOption';
import type {
  EntitySelectCategorizedSearchResult,
  EntitySelectConfig,
} from '../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../search/entitySearch/CategorizedEntitySelect';
import { groupToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import addTestId from '../utils/addTestId';
import handleApolloError from '../utils/error/handleApolloError';
import * as styles from './AddMemberSelect.module.scss';
import { useAllGroupMap } from './useAllGroups';

const ADD_MEMBER_SELECT_FRAGMENT = gql(`
  fragment AddMemberSelect_group on Group {
    id
    owners {
      id
    }
    members {
      id
    }
    children {
      id
    }
    allUsers {
      id
    }
  }
`);

export interface AddMemberSelectProps {
  group: DocumentType<typeof ADD_MEMBER_SELECT_FRAGMENT>;
  onAddGroup: (id: string) => Promise<unknown>;
  onAddUser: (id: string) => Promise<unknown>;
  onDismiss: () => void;
}

const getAllGroupChildren = (
  group: { id: string; children: { id: string }[] },
  allGroups: Record<string, { id: string; children: { id: string }[] }>,
): { id: string }[] =>
  _.compact([
    ...group.children,
    ...group.children.flatMap((child) => {
      const childGroup = allGroups[child.id];
      return childGroup ? getAllGroupChildren(childGroup, allGroups) : null;
    }),
  ]);

const getIdSet = (arr: ({ id: string | null } | null)[]) =>
  new Set(_.compact(arr.map((item) => item?.id)));

const AddMemberSelect = ({
  group,
  onAddGroup,
  onAddUser,
  onDismiss,
}: AddMemberSelectProps) => {
  const dispatch = useAppDispatch();
  const allGroups = useAllGroupMap();
  const getInvitationEmailOption = useInvitationEmailOption();

  const [input, setInput] = useState('');
  const hasInput = !!input;

  const [existingMemberIds, existingSubgroupIds] = useMemo(() => {
    const owners = group?.owners || [];
    const members = group?.members || [];
    const children = group?.children || [];
    const userIdSet = hasInput
      ? getIdSet([...members, ...owners])
      : getIdSet(group?.allUsers);
    const subgroupIdSet = hasInput
      ? getIdSet(children)
      : getIdSet(getAllGroupChildren(group, allGroups));

    return [userIdSet, subgroupIdSet];
  }, [allGroups, group, hasInput]);

  const config = useMemo<EntitySelectConfig>(
    () => ({
      user: {
        filter: (user) => !existingMemberIds.has(user.id),
        extra: () => ({ showBadge: true }),
        preferredMaxCount: 4,
      },
      group: {
        filter: (g) =>
          !!g?.id && g.id !== group?.id && !existingSubgroupIds.has(g.id),
        preferredMaxCount: 2,
      },
      new: {
        showIfNoOthersMatch: true,
        getGroup: (inputValue) =>
          getInvitationEmailOption({
            inputValue,
            onSelect: (suggestedEmail) => {
              dispatch(
                UIActions.openModal(InvitationModal, {
                  initialOptions: suggestedEmail
                    ? (inputToOptions(suggestedEmail) ?? undefined)
                    : undefined,
                  initialValues: { memberIds: [group.id] },
                }),
              );
            },
          }),
      },
      order: ['user', 'group', 'new'],
    }),
    [
      dispatch,
      existingMemberIds,
      getInvitationEmailOption,
      existingSubgroupIds,
      group?.id,
    ],
  );

  const handleAddUser = ({
    id,
    name,
  }: {
    id: string;
    name: string;
    type?: UserType | null;
  }) =>
    onAddUser(id).then(() =>
      dispatch(
        UIActions.pushToast(groupToasts.memberAdded({ userName: name })),
      ),
    );

  const handleAddGroup = ({ id, name }: { id: string; name: string }) =>
    onAddGroup(id)
      .then(() =>
        dispatch(
          UIActions.pushToast(groupToasts.subgroupAdded({ groupName: name })),
        ),
      )
      .catch((error) => {
        handleApolloError(error, {
          CIRCULAR_DESCENDANT: () => {
            dispatch(UIActions.pushToast(groupToasts.circularGroupError));
          },
        });
      });

  const handleAdd = (entity: EntitySelectCategorizedSearchResult | null) => {
    if (entity?.type === 'user') handleAddUser(entity.user);
    if (entity?.type === 'group') handleAddGroup(entity.group);
  };

  return (
    <div {...addTestId('addMemberSelect')} className={styles.container}>
      <CategorizedEntitySelect
        config={config}
        onChange={handleAdd}
        value={null}
        maxOptionsToShow={6}
        autoFocus
        inputValue={input}
        onInputChange={setInput}
        disableMenuPortal
        styles={{
          menu: (base) => ({
            ...base,
            minWidth: 260,
            right: 0,
          }),
        }}
      />
      <Button onClick={onDismiss} variant="muted">
        <div className={styles.buttonInner}>
          <SVGIcon size="small">{clearIcon}</SVGIcon>
        </div>
      </Button>
    </div>
  );
};

export default AddMemberSelect;
